import { useCallback, useMemo } from 'react';
import type { ValueLists } from '@ohpen/enso-ui';
import { ExportTemplateResponse, FirmGetAllResponse, firmsSqe, type FirmsSqeRequest } from '@pmp/api';
import { useSqeList } from '@pmp/hooks';
import type { SqePage, SqeQuery } from '@pmp/utils';

import { columns, firmSqeDefaultSort, firmSqeFields } from './tableConfig';
import { OPERATION_ORDER } from './utils';
import { EXPORT_ROWS, firmExportRows, toExportRows } from './exportRows';

// The shape the list columns render: the principal as the `{ key, value }` pair a value list reads, and operations
// in their display order. It carries the rows the firm exports as, which the list response alone gives.
const toListRow = (response: FirmGetAllResponse): FirmGetAllResponse => {
  const firm = {
    ...response,
    operationsWhitelist: [...response.operationsWhitelist].sort(
      (a, b) => OPERATION_ORDER.indexOf(a.key ?? '') - OPERATION_ORDER.indexOf(b.key ?? ''),
    ),
  };
  const listRow = {
    ...firm,
    principalFirm: firm.principalFirm ? { key: firm.principalFirm.id, value: firm.principalFirm.name ?? '' } : null,
  };
  return { ...listRow, [EXPORT_ROWS]: toExportRows(firm, listRow) } as unknown as FirmGetAllResponse;
};

const fetchFirmsPage = async (query: SqeQuery): Promise<SqePage<FirmGetAllResponse>> => {
  const { data } = await firmsSqe({ body: query as FirmsSqeRequest });
  return { items: data.items.map(toListRow), total: data.total };
};

export const useFirmsSqe = (valueLists?: ValueLists) => {
  const list = useSqeList(fetchFirmsPage, columns, firmSqeFields, valueLists, firmSqeDefaultSort);
  const { exportFetchAll: fetchListRows } = list;

  const exportFetchAll = useCallback(
    async (scope: 'view' | 'all', template?: ExportTemplateResponse) => firmExportRows(await fetchListRows(scope), template),
    [fetchListRows],
  );

  return useMemo(() => ({ ...list, exportFetchAll }), [list, exportFetchAll]);
};
